import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { CSVWrapper } from "commons";
import { convertToMoney } from "commons/src/Money";
import { format, now, FORMAT, DAY } from "commons/src/NuDateUtils";

import {
    PORTFOLIO_SUMMARY,
    ACCOUNT_SUMMARY,
    NAME,
    CLIENT_REFERENCE,
    JOINT_OWNER,
    ACCOUNT_NUMBER,
    WRAPPER_TYPE,
    VALUATION_AS_AT,
    PORTFOLIO_VALUE,
    ACCOUNT_VALUE,
    GAIN_LOSS,
    MONEY_IN,
    MONEY_OUT,
    INTER_ACCT_TRANSFER_IN,
    INTER_ACCT_TRANSFER_OUT,
    ADJUSTMENTS,
    INVESTMENTS,
    ASSET_NAME,
    ISIN,
    LAST_PRICE,
    QUANTITY,
    VALUE,
    PERCENTAGE_VALUE
} from "../../helpers/ReportConstants";
import { DownloadButton } from "./DownloadButton";

export function CSVGeneratorComponent(props) {
    const EMPTY_ROW = [" "];

    const percentageTo2DecimalPlaces = percentage => {
        if (!percentage) {
            return "N/A";
        }
        return percentage.toFixed(2) + "%";
    };

    const getReportHeader = isCombined => {
        return isCombined ? PORTFOLIO_SUMMARY : ACCOUNT_SUMMARY;
    };

    const getClientDetails = isJointAccount => {
        const reportSummary = [
            [NAME, props.name],
            [CLIENT_REFERENCE, props.clientId]
        ];
        if (isJointAccount) {
            return [...reportSummary, [JOINT_OWNER, props.jointHolderName]];
        }
        return reportSummary;
    };

    const getClientAccountDetails = (isCombined, isJointAccount) => {
        if (isCombined) {
            return [...getClientDetails(false), EMPTY_ROW];
        }
        return [
            ...getClientDetails(isJointAccount),
            [ACCOUNT_NUMBER, props.accountNumber],
            [WRAPPER_TYPE, props.accountType],
            EMPTY_ROW
        ];
    };

    const getValuationDateRow = () => {
        return [
            [VALUATION_AS_AT, format(now().subtract(1, DAY), FORMAT.SLASH_SEPARATED)],
            EMPTY_ROW
        ];
    };

    const getAccountSummaryValues = isCombined => {
        return [
            [isCombined ? PORTFOLIO_VALUE : ACCOUNT_VALUE, props.transactions[0].value],
            [GAIN_LOSS, props.transactions[1].value],
            [MONEY_IN, props.transactions[2].value],
            [MONEY_OUT, props.transactions[3].value],
            [INTER_ACCT_TRANSFER_IN, props.transactions[4].value],
            [INTER_ACCT_TRANSFER_OUT, props.transactions[5].value],
            [ADJUSTMENTS, props.transactions[6].value],
            EMPTY_ROW
        ];
    };

    const getInvestmentSection = (funds, isCombined) => {
        return [[INVESTMENTS], getInvestmentHeader(), ...getInvestments(funds, isCombined)];
    };

    const getInvestmentHeader = () => {
        return [ASSET_NAME, ISIN, QUANTITY, LAST_PRICE, VALUE, PERCENTAGE_VALUE];
    };

    const getInvestments = (funds, isCombined) => {
        return funds.map(f => [
            f.asset,
            f.isin,
            f.units,
            f.price,
            convertToMoney(f.value),
            percentageTo2DecimalPlaces(isCombined ? f.percentageOfPortfolio : f.percentageOfAccount)
        ]);
    };

    const generateCSV = () => {
        return [
            [getReportHeader(props.isCombined)],
            EMPTY_ROW,
            ...getClientAccountDetails(props.isCombined, props.isJointAccount),
            ...getValuationDateRow(),
            ...getAccountSummaryValues(props.isCombined),
            ...getInvestmentSection(props.funds, props.isCombined)
        ];
    };

    const triggerDownload = () => {
        try {
            props.exportToCsv(`${props.fileName}.csv`, generateCSV());
            props.setCsvError(false);
        } catch (error) {
            props.setCsvError(true);
        }
    };

    return (
        <DownloadButton data-testid="csv-download-button" onClick={triggerDownload}>
            CSV
        </DownloadButton>
    );
}

CSVGeneratorComponent.defaultProps = {
    setCsvError: () => {}
};

CSVGeneratorComponent.propTypes = {
    transactions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.number
        })
    ).isRequired,
    funds: PropTypes.arrayOf(
        PropTypes.shape({
            fund: PropTypes.shape({
                asset: PropTypes.string.isRequired,
                isin: PropTypes.string.isRequired,
                price: PropTypes.number.isRequired,
                percentageOfAccount: PropTypes.number.isRequired,
                percentageOfPortfolio: PropTypes.number.isRequired,
                units: PropTypes.number.isRequired,
                value: PropTypes.number.isRequired
            })
        })
    ).isRequired,
    name: PropTypes.string.isRequired,
    clientId: PropTypes.number.isRequired,
    accountNumber: PropTypes.string.isRequired,
    accountType: PropTypes.string.isRequired,
    isCombined: PropTypes.bool.isRequired,
    isJointAccount: PropTypes.bool.isRequired,
    jointHolderName: PropTypes.string,
    exportToCsv: PropTypes.func,
    setCsvError: PropTypes.func,
    fileName: PropTypes.string
};

const mapStateToProps = state => ({
    name: `${state.user.currentUser.forename} ${state.user.currentUser.surname}`,
    clientId: state.user.currentUser.clientId
});

export const CSVGenerator = connect(mapStateToProps)(CSVWrapper(CSVGeneratorComponent));
